/**
 * PAPPO — ImageCropper
 * Modal reutilizável que recebe um ficheiro de imagem, deixa o utilizador dar
 * zoom + arrastar e recortar num QUADRADO, e devolve o File recortado (avatares
 * e foto de grupo). Usa react-easy-crop + o helper de canvas getCroppedImg.
 */

import { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { getCroppedImg } from './cropImage';
import styles from './ImageCropper.module.css';

interface ImageCropperProps {
  /** Ficheiro de imagem escolhido pelo utilizador. */
  file: File;
  /** Chamado com o File recortado ao "Aplicar". */
  onApply: (cropped: File) => void;
  /** Chamado ao "Cancelar" (fecha sem alterar). */
  onCancel: () => void;
  /** Título do modal (padrão: "Enquadrar foto"). */
  title?: string;
}

export function ImageCropper({
  file,
  onApply,
  onCancel,
  title = 'Enquadrar foto',
}: ImageCropperProps) {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);

  /* Object URL do ficheiro — libertado ao desmontar / trocar de ficheiro. */
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setAreaPixels(pixels);
  }, []);

  const handleApply = useCallback(async () => {
    if (!areaPixels || busy) return;
    setBusy(true);
    try {
      const base = file.name.replace(/\.[^.]+$/, '') || 'avatar';
      const cropped = await getCroppedImg(imageSrc, areaPixels, `${base}.jpg`);
      onApply(cropped);
    } catch {
      setBusy(false);
    }
  }, [areaPixels, busy, imageSrc, file.name, onApply]);

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div className={styles.modal}>
        <h2 className={styles.title}>{title}</h2>

        <div className={styles.cropArea}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className={styles.controls}>
          <span className={styles.zoomLabel}>Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className={styles.zoomSlider}
            aria-label="Zoom"
          />
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.applyBtn}
            onClick={handleApply}
            disabled={busy || !areaPixels}
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}
